import { Component, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToastrMsgService } from 'src/app/shared/services/shared-services/toastr.service';

export interface RoadBoQ {
  road_boq_id: number;
  project_id: number;
  boq_code: string;
  boq_name: string;
  administrative_authority: string;
  road_type: string;
  lanes: number;
  right_of_way_meters: number;
  design_speed_kmh: number;
  road_length_km: number;
  pavement_type: string;
  quality_standard: string;
  status: string;
  total_amount: number;
  item_count: number;
  bom_item_count: number;
  project_name: string;
  created_at: string;
}

@Component({
  selector: 'app-road-boq-list',
  templateUrl: './road-boq-list.component.html',
  styleUrls: ['./road-boq-list.component.css']
})
export class RoadBoQListComponent implements OnInit, AfterViewInit {
  displayedColumns: string[] = [
    'boq_code',
    'boq_name',
    'administrative_authority',
    'road_type',
    'specifications',
    'status',
    'total_amount',
    'items',
    'actions'
  ];

  dataSource = new MatTableDataSource<RoadBoQ>([]);
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  loading = false;
  searchKeyword = '';
  selectedAuthority: string = '';
  selectedRoadType: string = '';
  selectedStatus: string = '';
  selectedProject: string = '';

  // Pagination
  totalItems = 0;
  pageSize = 10;
  pageIndex = 0;
  pageSizeOptions = [5, 10, 20, 50];

  // Filter options
  administrativeAuthorities = [
    'Central Govt (MoRTH / NHAI / NHIDCL)',
    'State Govt (PWD / R&B Depts)',
    'Local Govt (ZP / Panchayat / PMGSY)',
    'Urban Local Bodies (ULB / Municipal Corp.)',
    'Special Terrain & Environment (MoRTH + BRO + State PWD)'
  ];

  statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'draft', label: 'Draft' },
    { value: 'in_review', label: 'In Review' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'archived', label: 'Archived' }
  ];

  constructor(
    private apiService: ApiService,
    private toastrService: ToastrMsgService,
    private router: Router,
    private dialog: MatDialog
  ) {}

  ngOnInit() {
    this.loadRoadBoQs();
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
  }

  loadRoadBoQs() {
    this.loading = true;
    const params: any = {
      page: this.pageIndex + 1,
      limit: this.pageSize
    };

    if (this.searchKeyword) {
      params.search = this.searchKeyword;
    }

    if (this.selectedAuthority) {
      params.administrative_authority = this.selectedAuthority;
    }

    if (this.selectedRoadType) {
      params.road_type = this.selectedRoadType;
    }

    if (this.selectedStatus) {
      params.status = this.selectedStatus;
    }

    if (this.selectedProject) {
      params.project_id = this.selectedProject;
    }

    this.apiService.getRoadBoQs(params).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.dataSource.data = res.data;
          this.totalItems = res.pagination?.total || 0;
        } else {
          this.toastrService.error('Failed to load road BoQs');
        }
        this.loading = false;
      },
      error: (err: any) => {
        console.error('Error loading road BoQs:', err);
        this.toastrService.error(err.error?.error || 'Failed to load road BoQs');
        this.loading = false;
      }
    });
  }

  applyFilter() {
    this.pageIndex = 0;
    this.loadRoadBoQs();
  }

  onPageChange(event: PageEvent) {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadRoadBoQs();
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  }

  getStatusClass(status: string): string {
    const statusClasses: { [key: string]: string } = {
      'draft': 'bg-gray-100 text-gray-800',
      'in_review': 'bg-blue-100 text-blue-800',
      'approved': 'bg-green-100 text-green-800',
      'rejected': 'bg-red-100 text-red-800',
      'archived': 'bg-yellow-100 text-yellow-800'
    };
    return statusClasses[status] || 'bg-gray-100 text-gray-800';
  }

  viewDetails(roadBoQ: RoadBoQ) {
    this.router.navigate(['/home/road-boq', roadBoQ.road_boq_id]);
  }

  editRoadBoQ(roadBoQ: RoadBoQ) {
    this.router.navigate(['/home/road-boq', roadBoQ.road_boq_id, 'edit']);
  }

  deleteRoadBoQ(roadBoQ: RoadBoQ) {
    if (confirm(`Are you sure you want to delete "${roadBoQ.boq_name}"?`)) {
      this.apiService.deleteRoadBoQ(roadBoQ.road_boq_id).subscribe({
        next: (res: any) => {
          if (res.success) {
            this.toastrService.success('Road BoQ deleted successfully');
            this.loadRoadBoQs();
          }
        },
        error: (err: any) => {
          console.error('Error deleting road BoQ:', err);
          this.toastrService.error(err.error?.error || 'Failed to delete road BoQ');
        }
      });
    }
  }

  createNew() {
    this.router.navigate(['/home/road-boq/new']);
  }

  clearFilters() {
    this.searchKeyword = '';
    this.selectedAuthority = '';
    this.selectedRoadType = '';
    this.selectedStatus = '';
    this.selectedProject = '';
    this.pageIndex = 0;
    this.loadRoadBoQs();
  }

  getSpecifications(roadBoQ: RoadBoQ): string {
    const specs = [];
    if (roadBoQ.lanes) specs.push(`${roadBoQ.lanes} lanes`);
    if (roadBoQ.right_of_way_meters) specs.push(`ROW: ${roadBoQ.right_of_way_meters}m`);
    if (roadBoQ.design_speed_kmh) specs.push(`Speed: ${roadBoQ.design_speed_kmh} km/h`);
    if (roadBoQ.road_length_km) specs.push(`Length: ${roadBoQ.road_length_km} km`);
    return specs.join(' | ') || 'N/A';
  }
}

